import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { injectedLocalApi } from '@/services/local'
import { safeDeepUpdate } from '@/utils/object'

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface CurrentAction {
  type: string
  payload: {
    pageId: string
    previewToken: string
    sectionId: string
    action: string
  }
}

export interface MetaInitialState {
  previewMode: boolean
  themeSettingData: Customisation.ThemeSettingData
  sectionSettingData: Customisation.SectionSettingData
  siteSettingData: Customisation.SiteSettingData
  sortingSectionOrder: string[]
  sortingSectionId: string
  currentAction: CurrentAction
  siteId: string
  /**
   * Real intrinsic dimensions of section images, probed server-side during
   * layout prefetch and keyed by image URL. Lets image sections render true
   * width/height attributes instead of a guessed 600x600, so the browser
   * reserves the correct box and the image loading causes no layout shift.
   */
  imageDimensions: Record<string, { width: number; height: number }>
}

export const getMetaInitialState = (): PlainInfo<MetaInitialState> => ({
  status: 'none',
  data: {
    siteId: '',
    themeSettingData: {
      selectedPreset: '',
      settings: {
        colorSectionHeadingsFont: '',
        colorHeadingsFont: '',
        colorBodyFont: '',
        colorLinesAndBorders: '',
        colorContainerColor: '',
        colorBackgroundColor: '',
        typographyHeadings: '',
        typographyBodyText: '',
        buttonStyle: '',
        buttonShape: '',
        buttonTextColor: '',
        buttonColor: '',
      },
    },
    sectionSettingData: {
      _id: '',
      sections: {},
      pages: {},
    },
    siteSettingData: {
      homePageMetaDescription: '',
      homePageTitle: '',
      siteKeywordsTag: '',
    },
    sortingSectionOrder: [],
    sortingSectionId: '',
    currentAction: {} as CurrentAction,
    previewMode: false,
    imageDimensions: {},
  },
})

export const customisationSlice = createSlice({
  name: '@customisation',
  initialState: getMetaInitialState(),
  reducers: {
    setDefaultPageData: (
      state,
      action: PayloadAction<
        Oms.V4ApiResponse<Customisation.SectionSettingData>
      >,
    ) => {
      state.data.sectionSettingData._id = action.payload.data?._id || null
      state.data.sectionSettingData.pages = {
        ...state.data.sectionSettingData.pages,
        ...action.payload.data?.pages,
      }
      state.data.sectionSettingData.sections = {
        ...state.data.sectionSettingData.sections,
        ...action.payload.data?.sections,
      }
    },
    setImageDimensions: (
      state,
      action: PayloadAction<Record<string, { width: number; height: number }>>,
    ) => {
      state.data.imageDimensions = {
        ...state.data.imageDimensions,
        ...action.payload,
      }
    },
    setDefaultThemeSettings: (
      state,
      action: PayloadAction<Oms.V4ApiResponse<Customisation.Theme>>,
    ) => {
      const { siteId, themeSettingData, siteSettingData } =
        action.payload.data.themeData || {}
      state.data.siteId = siteId
      state.data.themeSettingData = themeSettingData
      state.data.siteSettingData = siteSettingData
    },
    updateCustomisationSetting: (
      state,
      action: PayloadAction<{
        themeSettingData: Customisation.ThemeSettingData
        sectionSettingData: Customisation.SectionSettingData
        sortingSectionOrder: string[]
        sortingSectionId: string
        currentAction: Record<string, unknown>
      }>,
    ) => {
      state.data.previewMode = true
      safeDeepUpdate(
        state.data,
        ['themeSettingData'],
        action.payload.themeSettingData,
      )
      safeDeepUpdate(
        state.data,
        ['sectionSettingData'],
        action.payload.sectionSettingData,
      )
      safeDeepUpdate(
        state.data,
        ['sortingSectionOrder'],
        action.payload.sortingSectionOrder,
      )
      safeDeepUpdate(
        state.data,
        ['sortingSectionId'],
        action.payload.sortingSectionId,
      )
      safeDeepUpdate(
        state.data,
        ['currentAction'],
        action.payload.currentAction,
      )
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        injectedLocalApi.endpoints.getTheme.matchFulfilled,
        (state, action) => {
          const { siteId, themeSettingData, siteSettingData } =
            action.payload.data.themeData || {}
          state.status = 'fulfilled'
          state.data.previewMode = Boolean(
            action.meta.arg.originalArgs.previewToken,
          )
          state.data.siteId = siteId
          state.data.themeSettingData = themeSettingData
          state.data.siteSettingData = siteSettingData
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.getPageData.matchFulfilled,
        (state, action) => {
          state.data.sectionSettingData._id = action.payload.data?._id || null
          state.data.sectionSettingData.pages = {
            ...state.data.sectionSettingData.pages,
            ...action.payload.data?.pages,
          }
          state.data.sectionSettingData.sections = {
            ...state.data.sectionSettingData.sections,
            ...action.payload.data?.sections,
          }
        },
      )
  },
})

export const {
  setDefaultPageData,
  setDefaultThemeSettings,
  setImageDimensions,
  updateCustomisationSetting,
} = customisationSlice.actions
